import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useOrg } from '@/contexts/OrgContext';
import { textLinkDangerVariants, textLinkVariants } from '@/components/ui/button-variants';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { cn } from '@/lib/cn';
import { apiPost } from '@/lib/api';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { RecurringTemplates } from '@/components/RecurringTemplates';

type Entry = {
  id: string;
  date: string;
  memo?: string;
  status: string;
  debitTotal?: string;
  creditTotal?: string;
};

export function JournalsPage() {
  const { t } = useTranslation();
  const { orgId } = useOrg();
  const online = useOnlineStatus();

  const mapDoc = useCallback(
    (id: string, data: Record<string, unknown>) =>
      ({ id, ...data }) as Entry,
    []
  );

  const {
    rows: entries,
    loading,
    pageIndex,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    pageSize,
    setPageSize,
    refresh
  } = useFirestorePageQuery<Entry>({
    orgId,
    collectionPath: 'journalEntries',
    orderByField: 'updatedAt',
    orderDirection: 'desc',
    mapDoc
  });

  async function reverseEntry(entryId: string) {
    if (!orgId || !online) return;
    await apiPost('/v1/journals/reverse', { orgId, entryId });
    refresh();
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{t('nav.journals')}</h1>
        <Button asChild>
          <Link to="/journals/new">{t('journals.new')}</Link>
        </Button>
      </div>
      <PaginatedTable
        className="mt-6"
        loading={loading}
        pageIndex={pageIndex}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={nextPage}
        onPrev={prevPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        empty={
          !entries.length ? (
            <p className="p-6 text-center text-sm text-[var(--fv-text-muted)]">
              {t('common.noResults')}
            </p>
          ) : undefined
        }
      >
        <table className="w-full text-sm">
          <thead className="bg-[var(--fv-bg)]">
            <tr>
              <th className="p-3 text-start">{t('journals.date')}</th>
              <th className="p-3 text-start">{t('journals.memo')}</th>
              <th className="p-3 text-start">{t('journals.status')}</th>
              <th className="p-3 text-end">{t('journals.amount')}</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-[var(--fv-border)]">
                <td className="p-3">{e.date}</td>
                <td className="p-3">{e.memo ?? '—'}</td>
                <td className="p-3">
                  <span
                    className={
                      e.status === 'posted'
                        ? 'text-[var(--fv-primary)]'
                        : 'text-[var(--fv-text-muted)]'
                    }
                  >
                    {e.status}
                  </span>
                </td>
                <td className="p-3 text-end"><MoneyDisplay value={e.debitTotal} /></td>
                <td className="p-3 text-end">
                  {e.status === 'draft' && (
                    <Link className={textLinkVariants()} to={`/journals/${e.id}`}>
                      {t('journals.edit')}
                    </Link>
                  )}
                  {e.status === 'posted' && !e.memo?.startsWith('Reversal') && (
                    <Button
                      type="button"
                      variant="link"
                      className={cn(textLinkDangerVariants(), 'ms-2 h-auto min-h-0')}
                      disabled={!online}
                      onClick={() => void reverseEntry(e.id)}
                    >
                      {t('journals.reverse')}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </PaginatedTable>
      <RecurringTemplates />
    </div>
  );
}
